/**
 * @author Ricardo Comendador Escalona
 * @version v0.0.1
 * @date  15/11/2021
 */
import React, {memo, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import styles from './index.less';
import {Card, Form, Input, InputNumber, Spin, Table} from 'antd';
import {LoadingOutlined} from '@ant-design/icons';
import TemplateDetailsFooterActions from '@/modules/templates/components/TemplateDetailsFooterActions';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {useCrudRunAudit} from '@/modules/auditorAudits/hooks/useAuditorAudits';
import {useCrudQuestions} from '@/modules/templates/hooks/useSections';
import {useResponsive} from '@/contexts/ResponsiveContext';
import {useRunAuditOnlineStatus} from '@/contexts/RunAuditOnlineStatusProvider';
import {isNumber} from 'lodash';
import InSituMeasureTitle from './InSituMeasureTitle';
import classNames from 'classnames';

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

export const EditableContext = React.createContext(null);

const EditableRow = ({...props }) => {
  const [form] = Form.useForm();

  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props}/>
      </EditableContext.Provider>
    </Form>
  );
};

export const EditableCell = ({
  // title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  question,
  sectionId,
  runAudit,
  placeholder,
  keyPos,
  isMobile,
  activeEdition,
  onActiveEdition,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  const {id} = useParams();
  const {create, isLoading: loading} = useCrudRunAudit();
  const {update, isLoading} = useCrudQuestions();
  const {online} = useRunAuditOnlineStatus();

  const {t} = useTranslation('templates');

  useEffect(() => {
    if (editing) {
      inputRef?.current?.focus();
    }
  }, [editing]);

  const loadValue = useCallback(() => {
    form.setFieldsValue({
      [dataIndex]: record?.[dataIndex],
    });
  }, [dataIndex, form, record]);

  const toggleEdit = useCallback(() => {
    setEditing(prev => !prev);
    loadValue();
  }, [loadValue]);

  useEffect(() => {
    if(activeEdition){
      setEditing(true);
      loadValue();
    }
  }, [activeEdition, loadValue, toggleEdit]);

  const initialValues = useMemo(() => {
    let text;
    if (dataIndex === 'place') {
      text = children?.[1] || t('placeNumber', {number: keyPos?.[0] + 1 || ''});
    } else {
      const value = children?.[1];
      text = isNumber(value) ? String(value).replace('.', ',') : '';
    }
    return [children?.[0], text];
  }, [children, dataIndex, keyPos, t]);

  const onUpdatePlace = useCallback(async (place) => {
    const idx = keyPos?.[0];
    const readingRegistry = question?.readingRegistry?.placesMeasure;

    readingRegistry[idx] = {...readingRegistry[idx], place, label: place};
    await update({sectionId, questionId: question?._id,...question, readingRegistry: {...question?.readingRegistry, placesMeasure: readingRegistry}});
  }, [keyPos, question, sectionId, update]);

  const save = useCallback(async(evt) => {
    const values = await form.validateFields();
    try {
      if (runAudit) {
        if (dataIndex === 'place') {
          form.setFieldsValue({
            [dataIndex]: values?.[dataIndex],
          });
          if (evt.target?.value !== record?.[dataIndex]) {
            await onUpdatePlace(values?.[dataIndex]);
            setEditing(prev => !prev);
            onActiveEdition?.(false);
            handleSave({...record, ...values});
            return;
          }
          setEditing(prev => !prev);
          onActiveEdition?.(false);
          return;
        }

        const templateAnswer = {
          type: 'readingRegistry',
          readingRegistry: {
            placesMeasure: record?._id,
            optionKey: dataIndex,
            value: values?.[dataIndex]// || record?.[dataIndex]
          }
        };
        if (Number(evt.target?.value) !== Number(record?.[dataIndex])) {
          await create({id, section: sectionId, question: question?._id, templateAnswer});
        }
      }

      toggleEdit();
      onActiveEdition?.(false);
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      if(!online){
        toggleEdit();
        onActiveEdition?.(false);
        handleSave({ ...record, ...values });
      }
    }
  }, [form, runAudit, toggleEdit, handleSave, record, dataIndex, onActiveEdition, onUpdatePlace, create, id, sectionId, question?._id, online]);

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Spin indicator={antIcon} spinning={loading || isLoading}>
        <Form.Item
          style={{
            margin: 0,
          }}
          name={dataIndex}
          className={classNames(styles.measureInput)}
        >
          {dataIndex === 'place' ? (
            <Input
              ref={inputRef}
              type={'text'}
              onPressEnter={save}
              onBlur={save}
              placeholder={placeholder}
              disabled={loading || isLoading}
              className={isMobile ? 'text-right' : 'text-left'}
            />
          ) : (
            <InputNumber
              ref={inputRef}
              onPressEnter={save}
              onBlur={save}
              placeholder={placeholder}
              disabled={loading || isLoading}
              decimalSeparator={','}
            />
          )
          }
        </Form.Item>
      </Spin>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: isMobile ? 0 : 12,
          color: dataIndex === 'place' ? record?.[dataIndex] ? '#000' : '#C9C7C1' : '#000',
          justifyContent: dataIndex === 'place' ? 'left' : 'right',
        }}
        onClick={toggleEdit}
      >
        {initialValues}
      </div>
    );
  }

  if(isMobile)
    return <div {...restProps}>{childNode}</div>;

  return <td {...restProps}>{childNode}</td>;
};

EditableCell.propTypes = {
  title: PropTypes.string,
  editable: PropTypes.bool,
  children: PropTypes.any,
  dataIndex: PropTypes.string,
  record: PropTypes.object,
  handleSave: PropTypes.func,
  runAudit: PropTypes.bool,
  sectionId: PropTypes.string,
  question: PropTypes.object,
  placeholder: PropTypes.string,
  keyPos: PropTypes.array,
  isMobile: PropTypes.bool,
  activeEdition : PropTypes.bool,
  onActiveEdition: PropTypes.func
};

const TemplateDetailsInSituMeasure = ({question, sectionId, runAudit}) => {
  const {t} = useTranslation('templates');
  const  {isMobile, isTablet} = useResponsive();

  const cols = useMemo(() =>
    question?.readingRegistry?.options?.map(item =>
      ({...item, title: item?.label, fixed: item?.key === '_id' && !isMobile && !isTablet ? 'left' : null, editable: true, dataIndex: item?.key})
    ) || [],[isMobile, isTablet, question?.readingRegistry?.options]);

  const readingRegistryResponses = useMemo(() => {
    return question?.answer?.templateAnswer?.readingRegistry || [];
  }, [question?.answer?.templateAnswer]);

  const placesMeasure = useMemo(() => {
    return question?.readingRegistry?.placesMeasure || [];
  }, [question?.readingRegistry]);

  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    const populateData = readingRegistryResponses?.length ? readingRegistryResponses?.reduce((result, item) => {
      const placeKey = placesMeasure.find((x) => x._id === item.placesMeasure)?._id;
      const placeObj = result.find((x) => x._id === placeKey);

      if (!placeObj) {
        result.push({
          _id: placeKey,
          [item.optionKey]: item.value,
        });
      } else {
        placeObj[item.optionKey] = item.value;
      }

      return result;
    }, placesMeasure) : placesMeasure;
    setDataSource(populateData);
  }, [placesMeasure, readingRegistryResponses]);

  const handleSave = useCallback((row) => {
    setDataSource(prev => {
      const index = prev.findIndex((item) => row.key === item.key);
      const item = prev[index];
      prev.splice(index, 1, { ...item, ...row });
      return prev?.map(x => x);
    });
  }, []);

  const columns = useMemo(() => cols.map((col) => {
    if (col.key === 'place') {
      return {
        ...col,
        dataIndex: col.key,
        fixed: isMobile || isTablet ? null : 'left',
        width: 200,
        onCell: (record, ...props) => {
          const keyPos = props;

          return ({
            record,
            editable: true,
            dataIndex: col.dataIndex,
            title: col.title,
            handleSave,
            question,
            sectionId,
            runAudit,
            ...col,
            keyPos,
            placeholder: t('placeNumber', {number: keyPos?.[0] + 1 || ''})
          });
        },
      };
    }

    return {
      ...col,
      width: 160,
      dataIndex: col.key,
      editable: true,
      title: <InSituMeasureTitle measure={col}/>,
      onCell: (record) => {
        return ({
          record,
          editable: true,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave,
          question,
          sectionId,
          runAudit
        });
      },
    };
  }), [cols, handleSave, isMobile, isTablet, question, runAudit, sectionId, t]);

  return (
    <Card
      className={styles.templateTextCard}
      id={'question-view-'+question?._id}
    >
      {/*<div style={{color: 'rgb(235, 0, 39)'}}>*/}
      {/*  {question?.controlParameter ? '*' : null}*/}
      {/*</div>*/}
      <div className="mb-3">
        {question?.name || question?.label}
      </div>
      <Form layout="vertical">
        <Form.Item name="nameInSituMeasure">
          <Table
            components={{
              body: {
                row: EditableRow,
                cell: EditableCell,
              },
            }}
            pagination={false}
            rowClassName={() => 'editable-row'}
            className={styles.inSituMeasureTable}
            dataSource={dataSource}
            columns={columns}
            scroll={{ x: 350, y: 350 }}
          />
        </Form.Item>
      </Form>

      <TemplateDetailsFooterActions question={question} sectionId={sectionId} runAudit={runAudit} />
    </Card>
  );
};

TemplateDetailsInSituMeasure.propTypes = {
  question: PropTypes.object,
  isCover: PropTypes.bool,
  runAudit: PropTypes.bool,
  sectionId: PropTypes.string,
};
TemplateDetailsInSituMeasure.defaultProps = {
  question: {},
};

export default memo(TemplateDetailsInSituMeasure);
